import type {
  OrganizationManager,
  VolunteerOrgActivityDomain,
  VolunteerOrgProject,
  VolunteerOrganization,
} from "@entities";
import {
  organizationManagerRepo,
  volunteerOrgProjectAssignmentRepo,
  volunteerOrganizationRepo,
} from "@repositories";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { CommunicationRecordManager } from "../../../components/CommunicationRecordManager/CommunicationRecordManager";
import { useEffect, useRef, useState } from "react";
import {
  VolunteerOrgContactsFields,
  VolunteerOrgDomainsPicker,
  VolunteerOrgEventFlagsFields,
  VolunteerOrgHandlingManagerField,
  VolunteerOrgLocationField,
} from "./VolunteerOrgSharedFields";
import { activityDomainIds, syncVolunteerOrgActivities } from "./sync-activities";
import { syncVolunteerOrgContacts } from "./sync-contacts";
import {
  canonicalVolunteerOrgContactColumns,
  carryVolunteerOrgActivation,
  clearedVolunteerOrgActivationFields,
  normalizeVolunteerOrgContactDrafts,
  defaultVolunteerOrgContactDrafts,
  volunteerOrgContactDrafts,
  volunteerOrgDisplayName,
  volunteerOrgFirstBranchSeed,
  type VolunteerOrgContactDraft,
} from "@shared/helpers/volunteer-organization";
import { VolunteerOrgAssignmentsPanel } from "./VolunteerOrgAssignmentsPanel";
import {
  dialogTabSx,
  FormSection,
  primaryActionSx,
  quietButtonSx,
} from "./volunteer-org-form-ui";

type Draft = {
  contacts: VolunteerOrgContactDraft[];
  name: string;
  comments: string;
  address: string;
  coordinates: { lat: number; lng: number } | null;
  volunteerCapacity: string;
  handlingManagerId: string;
  interestedInRoutine: boolean;
  interestedInOutbreak: boolean;
  interestedInEmergency: boolean;
  domainIds: string[];
};

const emptyDraft = (): Draft => ({
  name: "",
  comments: "",
  address: "",
  coordinates: null,
  volunteerCapacity: "",
  handlingManagerId: "",
  interestedInRoutine: false,
  interestedInOutbreak: false,
  interestedInEmergency: false,
  domainIds: [],
  contacts: defaultVolunteerOrgContactDrafts(),
});

export function VolunteerOrgBranchDialog({
  open,
  parent,
  branch,
  domains,
  projects,
  onClose,
  onSaved,
}: {
  open: boolean;
  parent: VolunteerOrganization | null;
  branch: VolunteerOrganization | null;
  domains: VolunteerOrgActivityDomain[];
  projects: VolunteerOrgProject[];
  onClose: () => void;
  onSaved: (saved: VolunteerOrganization) => void;
}) {
  const [tab, setTab] = useState(0);
  const continueToAssignments = useRef(false);
  const createdBranchId = useRef<string | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [handlingManager, setHandlingManager] =
    useState<OrganizationManager | null>(null);

  useEffect(() => {
    if (!open) {
      continueToAssignments.current = false;
      createdBranchId.current = null;
      return;
    }
    let cancelled = false;
    if (continueToAssignments.current && branch) {
      continueToAssignments.current = false;
      setTab(1);
    } else {
      setTab(0);
    }
    setError(null);
    if (!branch) {
      const inheritedId = parent?.handlingManagerId ?? "";
      setDraft({
        ...emptyDraft(),
        handlingManagerId: inheritedId,
      });
      setHandlingManager(parent?.handlingManager ?? null);
      if (!parent?.handlingManager && inheritedId) {
        void organizationManagerRepo
          .findId(inheritedId, { include: { organization: true } })
          .then((manager) => {
            if (!cancelled) setHandlingManager(manager ?? null);
          });
      }
      if (parent) {
        const source = parent;
        void volunteerOrganizationRepo
          .count({ parentId: source.id })
          .then((count) => {
            if (cancelled || count > 0) return;
            const seed = volunteerOrgFirstBranchSeed(source);
            setDraft((current) => {
              const carried = carryVolunteerOrgActivation(
                {
                  address: current.address,
                  coordinates: current.coordinates,
                  volunteerCapacity: current.volunteerCapacity.trim()
                    ? Number(current.volunteerCapacity)
                    : null,
                  interestedInRoutine: current.interestedInRoutine,
                  interestedInOutbreak: current.interestedInOutbreak,
                  interestedInEmergency: current.interestedInEmergency,
                },
                {
                  address: seed.address,
                  coordinates: seed.coordinates,
                  volunteerCapacity: seed.volunteerCapacity.trim()
                    ? Number(seed.volunteerCapacity)
                    : null,
                  interestedInRoutine: seed.interestedInRoutine,
                  interestedInOutbreak: seed.interestedInOutbreak,
                  interestedInEmergency: seed.interestedInEmergency,
                },
              );
              return {
                ...current,
                address: carried.address?.trim() ?? "",
                coordinates: carried.coordinates ?? null,
                volunteerCapacity:
                  carried.volunteerCapacity == null
                    ? ""
                    : String(carried.volunteerCapacity),
                interestedInRoutine: Boolean(carried.interestedInRoutine),
                interestedInOutbreak: Boolean(carried.interestedInOutbreak),
                interestedInEmergency: Boolean(carried.interestedInEmergency),
              };
            });
          });
      }
      return () => {
        cancelled = true;
      };
    }
    setDraft({
      name: branch.name ?? "",
      comments: branch.comments ?? "",
      address: branch.address ?? "",
      coordinates: branch.coordinates ?? null,
      volunteerCapacity:
        branch.volunteerCapacity == null ? "" : String(branch.volunteerCapacity),
      handlingManagerId: branch.handlingManagerId ?? "",
      interestedInRoutine: branch.interestedInRoutine,
      interestedInOutbreak: branch.interestedInOutbreak,
      interestedInEmergency: branch.interestedInEmergency,
      domainIds: activityDomainIds(branch.activities),
      contacts: volunteerOrgContactDrafts(branch),
    });
    setHandlingManager(branch.handlingManager ?? null);
    if (!branch.handlingManager && branch.handlingManagerId) {
      void organizationManagerRepo
        .findId(branch.handlingManagerId, { include: { organization: true } })
        .then((manager) => {
          if (!cancelled) setHandlingManager(manager ?? null);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [open, branch?.id, parent?.id]);

  const save = async () => {
    if (!parent) return;
    setSaving(true);
    setError(null);
    try {
      const contacts = normalizeVolunteerOrgContactDrafts(draft.contacts);
      const capacity = (() => {
        const raw = draft.volunteerCapacity.trim();
        if (!raw) return null;
        const value = Number(raw);
        if (!Number.isFinite(value) || value < 0) {
          throw new Error("קיבולת מתנדבים לא תקינה");
        }
        return Math.floor(value);
      })();
      let activation = {
        address: draft.address,
        coordinates: draft.coordinates,
        volunteerCapacity: capacity,
        interestedInRoutine: draft.interestedInRoutine,
        interestedInOutbreak: draft.interestedInOutbreak,
        interestedInEmergency: draft.interestedInEmergency,
      };
      const creating = !branch;
      const retryId = creating ? createdBranchId.current : null;
      if (creating) {
        const existing = await volunteerOrganizationRepo.count({
          parentId: parent.id,
          ...(retryId ? { id: { $ne: retryId } } : {}),
        });
        if (existing === 0) {
          const source = await volunteerOrganizationRepo.findId(parent.id);
          if (source) activation = carryVolunteerOrgActivation(activation, source);
        }
      }
      const payload = {
        name: draft.name,
        parentId: parent.id,
        comments: draft.comments,
        handlingManagerId: draft.handlingManagerId || null,
        address: activation.address,
        coordinates: activation.coordinates,
        volunteerCapacity: activation.volunteerCapacity,
        interestedInRoutine: Boolean(activation.interestedInRoutine),
        interestedInOutbreak: Boolean(activation.interestedInOutbreak),
        interestedInEmergency: Boolean(activation.interestedInEmergency),
        ...canonicalVolunteerOrgContactColumns(contacts),
      };
      const saved = branch
        ? await volunteerOrganizationRepo.update(branch.id, payload)
        : retryId
          ? await volunteerOrganizationRepo.update(retryId, payload)
          : await volunteerOrganizationRepo.insert(payload);
      if (creating) createdBranchId.current = saved.id;
      await syncVolunteerOrgActivities(saved.id, draft.domainIds);
      await syncVolunteerOrgContacts(saved.id, contacts);
      if (creating) {
        continueToAssignments.current = true;
        const siblings = await volunteerOrganizationRepo.count({
          parentId: parent.id,
          id: { $ne: saved.id },
        });
        if (siblings === 0) {
          const parentAssignments = await volunteerOrgProjectAssignmentRepo.find({
            where: { volunteerOrganizationId: parent.id },
            limit: 500,
          });
          for (const row of parentAssignments) {
            await volunteerOrgProjectAssignmentRepo.update(row.id, {
              volunteerOrganizationId: saved.id,
            });
          }
          await volunteerOrganizationRepo.update(
            parent.id,
            clearedVolunteerOrgActivationFields(),
          );
        }
      }
      onSaved(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : "שמירה נכשלה");
    } finally {
      setSaving(false);
    }
  };

  const title = parent
    ? volunteerOrgDisplayName(draft.name || "שלוחה חדשה", parent.name)
    : "שלוחה";

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth dir="rtl">
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          py: 1.5,
        }}
      >
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          {parent ? (
            <Typography color="text.secondary" sx={{ fontSize: 13 }}>
              שלוחה להפעלה
            </Typography>
          ) : null}
        </Stack>
        <IconButton onClick={onClose} aria-label="סגירה" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Tabs value={tab} onChange={(_, next) => setTab(next)} sx={dialogTabSx}>
          <Tab label="הפעלה" />
          {branch ? <Tab label="שיבוצים" /> : null}
          {branch ? <Tab label="תקשורת" /> : null}
        </Tabs>
        {tab === 0 ? (
          <Stack gap={2}>
            <FormSection title="מיקום וקיבולת">
              <Stack gap={1.5}>
                <TextField
                  size="small"
                  label="שם השלוחה"
                  required
                  value={draft.name}
                  onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                />
                <VolunteerOrgLocationField
                  required
                  value={{ address: draft.address, coordinates: draft.coordinates }}
                  onChange={(next) => setDraft({ ...draft, ...next })}
                />
                {!branch ? (
                  <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                    אחרי השמירה ממשיכים לשיבוץ לפרויקט ולמשימות.
                  </Typography>
                ) : null}
                <TextField
                  size="small"
                  label="קיבולת מתנדבים"
                  type="number"
                  value={draft.volunteerCapacity}
                  onChange={(event) =>
                    setDraft({ ...draft, volunteerCapacity: event.target.value })
                  }
                />
                <VolunteerOrgHandlingManagerField
                  value={handlingManager}
                  onChange={(manager) => {
                    setHandlingManager(manager);
                    setDraft({
                      ...draft,
                      handlingManagerId: manager?.id ?? "",
                    });
                  }}
                  helperText="אם ריק, יוצג מנהל הארגון האב"
                />
                <TextField
                  size="small"
                  label="הערות פנימיות"
                  multiline
                  minRows={2}
                  value={draft.comments}
                  onChange={(event) =>
                    setDraft({ ...draft, comments: event.target.value })
                  }
                />
              </Stack>
            </FormSection>
            <VolunteerOrgEventFlagsFields
              value={draft}
              onChange={(next) => setDraft({ ...draft, ...next })}
              description="אפשר לסמן כמה. בלי סימון נלקחים הסוגים של הארגון האב."
              emptyHint="בלי סימון נלקחים סוגי האירוע של הארגון האב."
            />
            <VolunteerOrgDomainsPicker
              domains={domains}
              selectedIds={draft.domainIds}
              onChange={(domainIds) => setDraft({ ...draft, domainIds })}
              description="נוסף על התחומים של הארגון האב."
            />
            <VolunteerOrgContactsFields
              value={draft.contacts}
              onChange={(contacts) => setDraft({ ...draft, contacts })}
              description="ריק נשאר ריק כאן, ובהפעלה נלקח אותו תפקיד מהארגון האב. איש קשר חופשי שייך רק לשלוחה."
            />
          </Stack>
        ) : null}
        {tab === 1 && branch ? (
          <VolunteerOrgAssignmentsPanel organization={branch} projects={projects} />
        ) : null}
        {tab === 2 && branch ? (
          <CommunicationRecordManager
            volunteerOrganizationId={branch.id}
            type="volunteer-organization"
            showAllRecords
            embedded
          />
        ) : null}
        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} sx={quietButtonSx}>
          סגירה
        </Button>
        <Button
          variant="contained"
          disabled={saving || !parent || !draft.name.trim() || !draft.address.trim()}
          onClick={() => void save()}
          sx={primaryActionSx}
        >
          {saving ? "שומר..." : branch ? "שמירה" : "שמירה והמשך לשיבוץ"}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
